/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import { Link } from "react-router";
import guidebookContentStyle from "../guidebookContentStyle";
import TextContainer from "../../Container/TextContainer";
import Layout from "../../../containers/Layouts/Layout";
import TeaserSlogan from "../../Teaser/TeaserSlogan";
import HighlightBox from "../../Box/HighlightBox/HighlightBox";
import QuestionsContactSection from "../QuestionsContactSection/QuestionsContactSection";
import DoubleButton from "../../Button/DoubleButton";
import { Helmet } from "react-helmet-async";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import backgroundTeaser from "../../../assets/img/guidebook/Teaser_Dauer_700px.jpg";
import Typography from "@mui/material/Typography";
import { Box, Link as MuiLink, Paper } from "@mui/material";
import { gaSet } from "../../../services/googleAnalyticsService";

const GuidebookDivorceDuration = () => {
  const title = "Ratgeber - Dauer einer Scheidung | Legalbird";

  gaSet({ title: title });

  return (
    <Layout backgroundType={"serviceworld"}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content="Wir geben Ihnen einen Überblick über den Ablauf und die Dauer einer Scheidung." />
      </Helmet>
      <Paper sx={guidebookContentStyle.paper}>
        <TeaserSlogan
          slogan={"Wie lange dauert eine Scheidung? Alles zum Ablauf"}
          backgroundImagePath={backgroundTeaser}
          height={300}
          headingColor={"#333333"}
        />
        <TextContainer>
          <p>
            Sie haben sich entschlossen, sich scheiden zu lassen und fragen sich, wann Sie geschieden sein werden? Wir geben Ihnen einen Überblick über den
            Ablauf einer einvernehmlichen Scheidung, damit Sie einschätzen können, wie lange Ihre Scheidung voraussichtlich dauern wird.
          </p>
          <hr />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Scheidungen sind sehr individuell und daher auch die Dauer
          </Typography>
          <p>
            Jede Scheidung ist unterschiedlich. Daher ist eine genaue Prognose zur Dauer der Scheidung nicht möglich. Entscheidend ist, dass es sich bei Ihrer
            Scheidung um eine einvernehmliche Scheidung handelt. Das heißt, dass Sie sich mit Ihrem Partner über alle{" "}
            <MuiLink component={Link} sx={guidebookContentStyle.link} to={"/ratgeber/familienrecht/wichtige-themen-ueberblick"}>
              Scheidungsfolgen{" "}
            </MuiLink>
            (d.h. Themen wie den Unterhalt, das Sorgerecht, etc.) geeinigt haben und dies nicht mehr per Anwalt vor Gericht geregelt werden muss.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Dauer einer einvernehmlichen Scheidung
          </Typography>
          <p>
            Grundlage dafür, dass die Scheidung überhaupt eingereicht werden und somit der Scheidungsprozess gestartet werden kann, ist, dass Sie als Ehepaar
            vorher ein Jahr getrennt gelebt haben (
            <MuiLink component={Link} sx={guidebookContentStyle.link} to={"/ratgeber/familienrecht/scheidungsvoraussetzungen"}>
              Trennungsjahr
            </MuiLink>
            ). Die Dauer der Scheidung kann durch eine frühzeitige Stellung des Antrages etwas verkürzt werden. Der Scheidungsantrag kann meist einige Wochen
            vor Ablauf des Trennungsjahres eingereicht werden. Denn das Gericht benötigt mindestens drei bis vier Monate bis es die Auskünfte zum Ausgleich der
            Rentenansprüche vom Versorgungsträger (z.B. der deutschen Rentenversicherung) erhält.
          </p>
          <p>
            Ab Einreichen des Scheidungsantrags dauert eine einvernehmliche Scheidung (mit{" "}
            <MuiLink component={Link} sx={guidebookContentStyle.link} to={"/ratgeber/familienrecht/versorgungsausgleich"}>
              Versorgungsausgleich
            </MuiLink>
            ) im Normalfall vier bis sechs Monate. Bei einer Ehe, die weniger als drei Jahre gedauert hat, oder wenn dies durch die Ehepartner schriftlich
            vereinbart wird, muss kein Versorgungsausgleich durchgeführt werden - dann verkürzt sich die Dauer auf einen bis drei Monate.
          </p>
          <p>
            Bei besonders komplizierten, nicht einvernehmlichen Scheidungen mit vielen offenen Streitpunkten, kann das Verfahren über mehrere Jahre andauern.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Ablauf einer einvernehmlichen Scheidung und wie Sie Zeit sparen können
          </Typography>
          <p>
            Der Ablauf ist grob zusammengefasst immer wie folgt:
            <br />
            Frühestens neun Monate nach Ihrer Trennung kann der Scheidungsantrag von einem Anwalt bei Gericht eingereicht werden. Das Gericht stellt dann dem
            Ehepartner den Scheidungsantrag zu und berechnet den Versorgungsausgleich. Daraufhin wird ein Scheidungstermin zugewiesen, an dem das
            Familiengericht die Scheidung durchführt.
          </p>
          <p>Wie lange die einzelnen Schritte dauern und wie Sie Zeit sparen können, erklären wir Ihnen nun noch im Detail.</p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            1. Der Scheidungsantrag
          </Typography>
          <p>
            Wenn Sie sich scheiden lassen möchten, muss ein Scheidungsantrag durch einen Anwalt erstellt und bei Gericht eingereicht werden. Für den Antrag
            müssen verschiedene Angaben zu Ihnen und Ihrem Ehepartner, Finanzen, der Ehe und Dokumente wie z.B. die Heiratsurkunde und Geburtsurkunden Ihrer
            Kinder zusammengestellt werden. Alle Angaben und Dokumente können Sie bei Legalbird direkt online hinterlegen, so dass kein Besuch beim Anwalt oder
            lästiges Verschicken per Post notwendig wird. So sparen Sie bei der Erstellung des Antrags deutlich Zeit ein.
          </p>
          <p>
            Wenn der Antrag vollständig ist, kann dieser durch den Anwalt bei Gericht eingereicht werden. Hier werden dann Kosten für den Anwalt und für das
            Gericht fällig.{" "}
            <MuiLink component={Link} sx={guidebookContentStyle.link} to={"/ratgeber/familienrecht/scheidungskosten"}>
              Wie hoch diese Kosten sind
            </MuiLink>
            , basiert auf den Angaben in Ihrem Scheidungsantrag. Damit das Gericht Ihren Antrag überhaupt bearbeitet, müssen Sie einen Gerichtskostenvorschuss
            zahlen. Sie sparen also deutlich Zeit ein, wenn Sie den Gerichtskostenvorschuss sofort zahlen, sobald Sie die Rechnung vom Gericht erhalten haben.
          </p>
          <HighlightBox headline={"Tipp 1: Gerichtskosten sofort zahlen"}>
            <p>
              Zahlen Sie den Gerichtskostenvorschuss sofort - denn erst dann beginnt das Gericht direkt mit Ihrer Scheidung. Je später Sie die Kosten
              überweisen, desto später beginnt der Scheidungsprozess.
            </p>
          </HighlightBox>
          <p>
            Wenn das Gericht den Gerichtskostenvorschuss erhalten hat, beginnt es mit der Bearbeitung und leitet den Scheidungsantrag an Ihren Ehepartner
            weiter. Ab Einreichen der Scheidung bis zur Zustellung des Scheidungsantrags beim Ehepartner vergehen in der Regel zwei bis drei Wochen. Daraufhin
            berechnet das Gericht den Versorgungsausgleich.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            2. Der Versorgungsausgleich
          </Typography>
          <p>
            Der Versorgungsausgleich kann, wenn der Rentenverlauf von Ihnen und Ihrem Partner nicht vollständig geklärt ist, sehr lange dauern. Zwischen drei
            und sechs Monate benötigt das Gericht, um alle notwendigen Informationen einzuholen. Die gute Nachricht: Sie können dafür sorgen, dass der
            Versorgungsausgleich deutlich schneller geht:
          </p>
          <p>
            Klären Sie so früh wie möglich, d.h. bestenfalls schon während des Trennungsjahrs, ob das Rentenkonto und somit alle Informationen zu Ihren
            Rentenansprüchen und denen Ihres Partners aktuell sind. Damit das Gericht einen Versorgungsausgleich durchführen kann, müssen alle Rentenansprüche
            ab dem 18. Lebensjahr geklärt sein. Es werden auch alle Ansprüche, die Sie während der Schulausbildung, der Mutterschutzzeit oder einer
            Arbeitslosigkeit erworben haben, berücksichtigt. Oft fehlen Angaben zu solchen Zeiten in Ihrem Rentenkonto. Dann müssen diese Zeiten nachträglich
            noch erfasst werden. Dieses Einholen von fehlenden Angaben nimmt erhebliche Zeit in Anspruch, da der Versorgungsträger Kontakt mit Ihnen aufnehmen
            und Sie zur Klärung der noch nicht berücksichtigten Zeiten auffordern muss.
          </p>
          <Box sx={guidebookContentStyle.scrollToAnchor} id={"rentenansprueche-klaeren"} />
          <HighlightBox headline={"Tipp 2: Rentenansprüche so früh wie möglich klären"}>
            <p>
              Um ungeklärte Zeiträume und damit deutliche Verzögerungen zu vermeiden, sollten Sie und Ihr Partner so schnell wie möglich beim zuständigen Träger
              einen Antrag auf Kontenklärung stellen. In den meisten Fällen ist der zuständige Träger die Deutsche Rentenversicherung. Ebenso sollten die
              ausgefüllten Formulare für den Versorgungsausgleich bereits dem Scheidungsantrag und bei der Zustimmung zur Scheidung (von Ihrem Partner)
              beigefügt werden.
            </p>
            <p>
              Sind Sie bei der Bundesversicherungsanstalt für Angestellte versichert, lassen Sie sich dazu in einer örtlichen Beratungsstelle der BfA beraten.
              Sie finden diese Beratungsstellen in allen größeren Städten. Berücksichtigen Sie, dass auch hier Wartezeiten von mehreren Wochen bestehen, bis Sie
              einen Termin erhalten.
            </p>
          </HighlightBox>
          <p>
            Wenn Sie bisher keine Kontenklärung bei Ihrer Rentenversicherung angefragt haben, sollten Sie und Ihr Partner zur Beschleunigung des
            Versorgungsausgleichs das folgende Formular frühzeitig ausfüllen und uns zukommen lassen. Wir fügen dieses dann Ihrem Scheidungsantrag bei. Ihr
            Partner sollte sein Formular dann bei seiner Zustimmung zur Scheidung an das Gericht hinzufügen.
          </p>
          <Grid container>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <Button
                component={"a"}
                href={"/documents/Formular_Versorgungsausgleich.pdf"}
                download={"Formular_Versorgungsausgleich"}
                sx={guidebookContentStyle.downloadButton}
                fullWidth
                variant={"text"}
              >
                Formular herunterladen
              </Button>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <Button
                component={"a"}
                href={"/documents/Hinweise_Versorgungsausgleich.pdf"}
                download={"Hinweise_Versorgungsausgleich"}
                sx={guidebookContentStyle.downloadButton}
                fullWidth
                variant={"text"}
              >
                Hinweise zum Formular
              </Button>
            </Grid>
          </Grid>
          <p>Sie leben in einer Lebenspartnerschaft? Dann müssen Sie folgendes Formular ausfüllen:</p>
          <Grid container sx={{
            justifyContent: "flex-end"
          }}>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <Button
                component={"a"}
                href={"/documents/Formular_Versorgungsausgleich_Lebenspartnerschaft.pdf"}
                download={"Formular_Versorgungsausgleich_Lebenspartnerschaft"}
                sx={guidebookContentStyle.downloadButton}
                fullWidth
                variant={"text"}
              >
                Formular herunterladen
              </Button>
            </Grid>
          </Grid>
          <p>
            Sie befinden sich noch im Trennungsjahr und möchten frühzeitig eine Kontenklärung beantragen? Hier können Sie das Formular zur Kontenklärung
            herunterladen:
          </p>
          <Grid container sx={{
            justifyContent: "flex-end"
          }}>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <Button
                component={"a"}
                href={"/documents/Kontenklaerung_Rentenversicherung.pdf"}
                download={"Kontenklaerung_Rentenversicherung"}
                sx={guidebookContentStyle.downloadButton}
                fullWidth
                variant={"text"}
              >
                Formular herunterladen
              </Button>
            </Grid>
          </Grid>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            3. Scheidungstermin und rechtskräftige Scheidung
          </Typography>
          <p>
            Wenn das Gericht den Versorgungsausgleich berechnet hat, teilt es Ihnen den Scheidungstermin mit. Beim Scheidungstermin müssen Sie, Ihr Partner und
            der Anwalt persönlich anwesend sein. Wie der Termin im Detail verläuft, erfahren Sie{" "}
            <MuiLink component={Link} sx={guidebookContentStyle.link} to={"/ratgeber/familienrecht/gerichtstermin"}>
              hier
            </MuiLink>
            .
          </p>
          <p>
            Der Scheidungstermin dauert ca. 10-15 Minuten. Im Termin fällt der Richter das Urteil über Ihre Scheidung. Spätestens 6 Wochen nach dem
            Scheidungstermin erhalten Sie und Ihr Partner den rechtskräftigen Scheidungsbeschluss und Sie sind damit rechtskräftig geschieden.
          </p>
          <p>
            Sie wollen jetzt mit Ihrer Scheidung loslegen, oder haben den Scheidungsantrag noch nicht zu Ende ausgefüllt? Wenden Sie die Tipps aus diesem
            Artikel an und lassen Sie uns jetzt ganz einfach online alle Angaben und Dokumente zukommen - dann steht einer möglichst schnellen und einfachen
            Scheidung nichts mehr im Weg!
          </p>
          <DoubleButton buttonText={"zum Scheidungsantrag"} buttonLink={"/servicewelt"} backText={"Zurück zum Ratgeber"} backLink={"/ratgeber/familienrecht"} />
          <QuestionsContactSection />
        </TextContainer>
      </Paper>
    </Layout>
  );
};

export default GuidebookDivorceDuration;
